import axios from 'axios';
import constant from "../constants/constant";
console.log("Constant", constant);
const api = axios.create({
  baseURL: constant.API_BASE_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    let token = null;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const url = config.url || '';

    if (url.startsWith('/manager') || path.startsWith('/manager')) {
      token = sessionStorage.getItem('managerToken');
    } else if (url.startsWith('/bd') || path.startsWith('/bd')) {
      token = sessionStorage.getItem('bdToken');
    } else if (url.startsWith('/agencies') || url.startsWith('/agency') || path.startsWith('/agency')) {
      token = sessionStorage.getItem('agencyToken');
    } else if (url.startsWith('/seller') || path.startsWith('/seller')) {
      token = sessionStorage.getItem('sellerToken');
    }

    if (!token) {
      token = sessionStorage.getItem('adminToken') || 
              sessionStorage.getItem('managerToken') || 
              sessionStorage.getItem('bdToken') || 
              sessionStorage.getItem('agencyToken') || 
              sessionStorage.getItem('sellerToken');
    }

    if (token && token !== 'true') {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('adminToken');
      sessionStorage.removeItem('agencyToken');
      sessionStorage.removeItem('sellerToken');
      sessionStorage.removeItem('bdToken');
      sessionStorage.removeItem('managerToken');
      const isManager = window.location.pathname.startsWith('/manager');
      const isBD = window.location.pathname.startsWith('/bd');
      const isAgency = window.location.pathname.startsWith('/agency');
      const isSeller = window.location.pathname.startsWith('/seller');
      window.location.href = isManager ? '/manager/login' : (isBD ? '/bd/login' : (isSeller ? '/seller/login' : (isAgency ? '/agency/login' : '/login')));
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getProfile: () => api.get('/auth/getuser'),
  getRoles: () => api.get('/auth/roles'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const usersAPI = {
  getUsers: (params) => api.get('/auth/users', { params }),
  getUser: (id) => api.get(`/auth/users/${id}`),
  createUser: (data) => api.post('/auth/create-user', data),
  updateUser: (id, data) => api.put(`/auth/${id}`, data),
  deleteUser: (id, reason) => api.delete(`/auth/${id}`, { data: { reason } }),
  getDeletedUsers: () => api.get('/auth/deleted-users'),
  restoreUser: (id) => api.post(`/auth/${id}/restore`),
  updateUserLevel: (id, data) => api.put(`/admin/users/${id}/level`, data),
  banUser: (id, data) => api.put(`/admin/business/users/${id}/ban`, data),
  customizeId: (id, data) => api.put(`/auth/users/${id}/custom-id`, data),
};

export const permissionsAPI = {
  getModules: () => api.get('/permissions/modules'),
  createModule: (data) => api.post('/permissions/modules', data),
  deleteModule: (key) => api.delete(`/permissions/modules/${key}`),
  getMatrix: () => api.get('/permissions/matrix'),
  updateRolePermissions: (roleName, data) => api.put(`/permissions/role/${roleName}`, data),
  getMyPermissions: () => api.get('/permissions/my-permissions')
};

export const sessionsAPI = {
  getActiveSessions: () => api.get('/admin/sessions/active'),
  getSessions: (params) => api.get('/admin/sessions', { params }),
  getSession: (id) => api.get(`/admin/sessions/${id}`),
  endSession: (id) => api.put(`/admin/sessions/${id}/end`),
  getSessionStats: () => api.get('/admin/sessions/stats'),
};

export const giftsAPI = {
  getGifts: (params) => api.get('/admin/gifts', { params }),
  getActiveGifts: () => api.get('/admin/gifts/active'),
  getGift: (id) => api.get(`/admin/gifts/${id}`),
  createGift: (data) => api.post('/admin/gifts', data),
  updateGift: (id, data) => api.put(`/admin/gifts/${id}`, data),
  deleteGift: (id) => api.delete(`/admin/gifts/${id}`),
  activateGift: (id) => api.put(`/admin/gifts/${id}/activate`),
  deactivateGift: (id) => api.put(`/admin/gifts/${id}/deactivate`),
  getGiftStats: () => api.get('/admin/gifts/stats'),
  uploadGiftImage: (formData) => api.post('/admin/gifts/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const customIdAPI = {
  getRequests: () => api.get('/admin/custom-id-requests'),
  offerPrice: (id, priceCoins) => api.put(`/admin/custom-id-requests/${id}/offer`, { price_coins: priceCoins }),
  rejectRequest: (id) => api.put(`/admin/custom-id-requests/${id}/reject`),
};

export const walletsAPI = {
  getWallets: (params) => api.get('/admin/wallets', { params }),
  getWallet: (userId) => api.get(`/admin/wallets/user/${userId}`),
  updateWalletBalance: (userId, data) => api.put(`/admin/wallets/${userId}/update-balance`, data),
  addCoins: (userId, data) => api.post(`/admin/wallets/${userId}/add-coins`, data),
  deductCoins: (userId, data) => api.post(`/admin/wallets/${userId}/deduct-coins`, data),
  addDiamonds: (userId, data) => api.post(`/admin/wallets/${userId}/add-diamonds`, data),
  deductDiamonds: (userId, data) => api.post(`/admin/wallets/${userId}/deduct-diamonds`, data),
  getTransactions: (userId, params) => api.get(`/admin/wallets/${userId}/transactions`, { params }),
  getAllTransactions: (params) => api.get('/admin/wallets/transactions', { params }),
  getWalletStats: () => api.get('/admin/wallets/stats'),
  getTransactionStats: () => api.get('/admin/wallets/transactions/stats'),
  getExchangeSlabs: () => api.get('/users/exchange-slabs'),
  updateExchangeSlabs: (data) => api.put('/admin/wallets/exchange-slabs', data),
  getCoinSlabs: () => api.get('/admin/wallets/coin-slabs'),
  updateCoinSlabs: (data) => api.put('/admin/wallets/admin/coin-slabs', data),
};

export const reportsAPI = {
  getDailyReport: (params) => api.get('/admin/reports/daily', { params }),
  getMonthlyReport: (params) => api.get('/admin/reports/monthly', { params }),
  getUserGrowthReport: (params) => api.get('/admin/reports/users', { params }),
  getRevenueReport: (params) => api.get('/admin/reports/revenue', { params }),
  getSessionReport: (params) => api.get('/admin/reports/sessions', { params }),
  getGiftsReport: (params) => api.get('/admin/reports/gifts', { params }),
  getAgenciesReport: (params) => api.get('/admin/reports/agencies', { params }),
  getCoinsReport: (params) => api.get('/admin/reports/coins', { params }),
  getDashboardSummary: () => api.get('/admin/reports/dashboard-summary'),
};

export const withdrawalsAPI = {
  getWithdrawals: (params) => api.get('/admin/withdrawals', { params }),
  getStats: () => api.get('/admin/withdrawals/stats'),
  updateStatus: (id, data) => api.put(`/admin/withdrawals/${id}/status`, data),
  rejectWithdrawal: (id, data) => api.post(`/admin/withdrawals/${id}/reject`, data),
  getConfig: () => api.get('/admin/withdrawals/config'),
  updateConfig: (data) => api.put('/admin/withdrawals/config', data),
  getTopSellers: () => api.get('/wallet/top-sellers'),
  getWithdrawalTargetSlabs: () => api.get('/admin/withdrawals/withdrawal-slabs'),
  updateWithdrawalTargetSlabs: (data) => api.put('/admin/withdrawals/withdrawal-slabs', data),
};

export const agenciesAPI = {
  login: (identifier, password) => api.post('/agencies/login', { identifier, password }),
  verifyOtp: (agency_id, otp) => api.post('/agencies/verify-otp', { agency_id, otp }),
  getAgencies: (params) => api.get('/agencies', { params }),
  createAgency: (data) => api.post('/agencies', data),
  getAgency: (id) => api.get(`/agencies/${id}`),
  updateAgency: (id, data) => api.put(`/agencies/admin/${id}`, data),
  updateAgencyOrder: (id, order_no) => api.put(`/agencies/admin/${id}`, { order_no }),
  updateAgencyStatus: (id, status, target_agency_id) => api.put(`/agencies/${id}/status`, { status, target_agency_id }),
  deleteAgency: (id, target_agency_id) => api.delete(`/agencies/${id}`, { data: { target_agency_id } }),
  overrideHostStay: (data) => api.post('/agencies/admin/override-host-stay', data),
  getDashboardStats: () => api.get('/agencies/dashboard/stats'),
  rechargeUser: (data) => api.post('/agencies/recharge', data),
  requestRechargeOTP: () => api.post('/agencies/recharge/request-otp'),
  getUsersList: (search) => api.get('/agencies/users/list', { params: { search } }),
  getRechargeHistory: () => api.get('/agencies/recharge/history'),
  topUpWallet: (amount) => api.post('/agencies/topup', { amount }),
  changePassword: (data) => api.put('/agencies/change-password', data),
  getHostApplications: () => api.get('/agencies/hosts/applications'),
  updateHostApplication: (id, status) => api.put(`/agencies/hosts/applications/${id}`, { status }),
  getAgencyHosts: (agencyId) => api.get(`/agencies/${agencyId}/hosts`),
  transferBD: (agencyId, data) => api.post(`/agencies/${agencyId}/transfer-bd`, data),
  getBDTransfers: (agencyId) => api.get(`/agencies/${agencyId}/bd-transfers`),
  getTickets: (params) => api.get('/agencies/tickets', { params }),
  createTicket: (data) => api.post('/agencies/tickets', data),
  replyTicket: (id, data) => api.post(`/agencies/tickets/${id}/reply`, data),
};

export const momentsAPI = {
  getMoments: (params) => api.get('/admin/moments', { params }),
  getMoment: (id) => api.get(`/admin/moments/${id}`),
  updateMoment: (id, data) => api.put(`/admin/moments/${id}`, data),
  deleteMoment: (id) => api.delete(`/admin/moments/${id}`),
  getCategories: () => api.get('/admin/moments/categories'),
  updateStatus: (id, data) => api.put(`/admin/moments/${id}/status`, data),
};

export const roomsAPI = {
  getActiveRooms: () => api.get('/rooms'), // Gets active rooms
  getAllRooms: () => api.get('/rooms/all'), // Gets all rooms (Admin only)
  endRoom: (id) => api.delete(`/rooms/${id}`), // Force end room
  getLevelConfigs: () => api.get('/rooms/level-configs'),
  saveLevelConfigs: (configs, replaceAll = true) => api.post('/rooms/level-configs', { configs, replaceAll }),
  deleteLevelConfig: (level) => api.delete(`/rooms/level-configs/${level}`),
  updateRoomLevel: (id, data) => api.put(`/rooms/${id}/level`, data),
  getRoomLimits: (id) => api.get(`/rooms/${id}/level-limits`),
  getRoomBonuses: () => api.get('/rooms/admin/room-bonuses'),
  getHiddenRoomGlobalPricing: () => api.get('/admin/hidden-rooms/pricing'),
  updateHiddenRoomGlobalPricing: (data) => api.post('/admin/hidden-rooms/pricing', data),
  getHiddenRoomsList: () => api.get('/admin/hidden-rooms/rooms'),
  updateRoomHiddenPricing: (roomId, data) => api.post(`/admin/hidden-rooms/${roomId}/pricing`, data),
};

export const storeAPI = {
  getItems: (category) => api.get('/store/items', { params: { category } }),
  createItem: (data) => api.post('/store', data),
  updateItem: (id, data) => api.put(`/store/${id}`, data),
  deleteItem: (id) => api.delete(`/store/${id}`),
};

export const feedbackAPI = {
  getFeedback: () => api.get('/feedback'),
  resolveFeedback: (id, status) => api.put(`/feedback/${id}/resolve`, { status }),
};

export const chatModerationAPI = {
  getReports: (params) => api.get('/admin/chat-reports', { params }),
  takeAction: (reportId, data) => api.post(`/admin/chat-reports/${reportId}/action`, data),
};

export const sellerAPI = {
  login: (identifier, password) => api.post('/seller/login', { identifier, password }),
  getProfile: () => api.get('/seller/me'),
  getDashboardStats: () => api.get('/seller/dashboard-stats'),
  searchUser: (q) => api.get('/seller/search-user', { params: { q } }),
  searchRecipients: (q, type) => api.get('/seller/search-recipients', { params: { q, type } }),
  transferCoins: (data) => api.post('/seller/transfer-coins', data),
  getTransactions: (params) => api.get('/seller/transactions', { params }),
  getRate: () => api.get('/seller/rate'),
  updateRate: (data) => api.put('/seller/rate', data),
  getWithdrawalRequests: (params) => api.get('/seller/withdrawal-requests', { params }),
  acceptWithdrawalRequest: (id) => api.put(`/seller/withdrawal-requests/${id}/accept`),
  payWithdrawalRequest: (id, data) => api.put(`/seller/withdrawal-requests/${id}/pay`, data),
  rejectWithdrawalRequest: (id, data) => api.put(`/seller/withdrawal-requests/${id}/reject`, data),
  getChats: () => api.get('/seller/chats'),
  getChatMessages: (userId) => api.get(`/seller/chat/${userId}`),
  sendChatMessage: (userId, data) => api.post(`/seller/chat/${userId}/send`, data),
  raiseDispute: (data) => api.post('/seller/disputes/raise', data),
  uploadDisputeProof: (formData) => api.post('/seller/disputes/upload-proof', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDisputes: () => api.get('/seller/disputes'),
  getCoinSlabs: () => api.get('/seller/coin-slabs'),
  verifyRechargePayment: (data) => api.post('/seller/recharge/verify', data),
};

export const sellerRateAdminAPI = {
  getRateConfig: () => api.get('/admin/business/sellers/rate-config'),
  updateRateConfig: (data) => api.put('/admin/business/sellers/rate-config', data),
  updateSellerRate: (id, data) => api.put(`/admin/business/sellers/${id}/rate`, data),
  getSellerCoinSlabs: () => api.get('/admin/business/seller-coin-slabs'),
  updateSellerCoinSlabs: (data) => api.put('/admin/business/admin/seller-coin-slabs', data),
  deductSellerBalance: (id, data) => api.post(`/admin/business/sellers/${id}/deduct-balance`, data),
  getSellerDisputes: () => api.get('/admin/business/seller-disputes'),
  resolveSellerDispute: (id, data) => api.post(`/admin/business/seller-disputes/${id}/resolve`, data),
};

export const levelRewardsAPI = {
  getLevelRewards: (params) => api.get('/admin/level-rewards', { params }),
  createOrUpdateLevelReward: (data) => api.post('/admin/level-rewards', data),
  deleteLevelReward: (id) => api.delete(`/admin/level-rewards/${id}`),
  equipItem: (data) => api.post('/user/equip-item', data),
  uploadRewardImage: (formData) => api.post('/admin/level-rewards/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const bdAPI = {
  login: (identifier, password) => api.post('/bd/login', { identifier, password }),
  getProfile: () => api.get('/bd/me'),
  getDashboard: () => api.get('/bd/dashboard'),
  getAgencies: (params) => api.get('/bd/agencies', { params }),
  getAgency: (id, params) => api.get(`/bd/agencies/${id}`, { params }),
  createAgency: (data) => api.post('/bd/agencies', data),
  getHosts: (params) => api.get('/bd/hosts', { params }),
  getHostDetails: (id, params) => api.get(`/bd/hosts/${id}/details`, { params }),
  getPerformance: (params) => api.get('/bd/performance', { params }),
  getCommissions: (params) => api.get('/bd/commissions', { params }),
  changePassword: (data) => api.put('/bd/change-password', data),
  getTickets: (params) => api.get('/bd/tickets', { params }),
  createTicket: (data) => api.post('/bd/tickets', data),
  replyTicket: (id, data) => api.post(`/bd/tickets/${id}/reply`, data),
  inviteAgency: (data) => api.post('/bd/invite-agency', data),
  getInvitations: () => api.get('/bd/invitations'),
};

export const managerAPI = {
  login: (identifier, password) => api.post('/manager/login', { identifier, password }),
  getProfile: () => api.get('/manager/me'),
  getDashboard: () => api.get('/manager/dashboard'),
  getBDs: (params) => api.get('/manager/bds', { params }),
  getBD: (id, params) => api.get(`/manager/bds/${id}`, { params }),
  createBD: (data) => api.post('/manager/bds', data),
  getAgencies: (params) => api.get('/manager/agencies', { params }),
  getAgency: (id, params) => api.get(`/manager/agencies/${id}`, { params }),
  getHosts: (params) => api.get('/manager/hosts', { params }),
  getHostDetails: (id, params) => api.get(`/manager/hosts/${id}/details`, { params }),
  getPerformance: (params) => api.get('/manager/performance', { params }),
  getCommissions: (params) => api.get('/manager/commissions', { params }),
  changePassword: (data) => api.put('/manager/change-password', data),
  getTickets: (params) => api.get('/manager/tickets', { params }),
  createTicket: (data) => api.post('/manager/tickets', data),
  replyTicket: (id, data) => api.post(`/manager/tickets/${id}/reply`, data),

  // Executive Manager Permissions & Country-Locked Management
  getUsers: (params) => api.get('/manager/users', { params }),
  uploadProof: (formData) => api.post('/manager/upload-proof', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  banUser: (id, data) => api.post(`/manager/users/${id}/ban`, data),
  unbanUser: (id, data) => api.post(`/manager/users/${id}/unban`, data),
  unbanUserWithCoins: (id, data) => api.post(`/manager/users/${id}/unban-with-coins`, data),
  inviteBD: (data) => api.post('/manager/invite-bd', data),
  getBDInvites: () => api.get('/manager/bd-invites'),
  getRooms: () => api.get('/manager/rooms'),
  closeRoom: (id) => api.delete(`/manager/rooms/${id}`),
  approveHost: (id, data) => api.put(`/manager/hosts/${id}/approval`, data),
  approveAgency: (id, data) => api.put(`/manager/agencies/${id}/approval`, data),
  getGifts: () => api.get('/manager/gifts'),
  getStoreItems: () => api.get('/manager/store-items'),
  assignItem: (data) => api.post('/manager/assign-item', data),
  assignVip: (data) => api.post('/manager/assign-vip', data),
  getSalaries: () => api.get('/manager/salaries'),
  getWithdrawals: () => api.get('/manager/withdrawals'),
  getReports: () => api.get('/manager/reports'),
  getAuditLogs: () => api.get('/manager/audit-logs'),
};

export const bannersAPI = {
  getBanners: (params) => api.get('/banners', { params }),
  getAdminBanners: (params) => api.get('/admin/banners', { params }),
  createBanner: (formData) => api.post('/banners', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateBanner: (id, formData) => api.put(`/banners/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteBanner: (id) => api.delete(`/banners/${id}`),
};

export const roomThemesAPI = {
  getThemes: (params) => api.get('/rooms/themes', { params }),
  getFreeThemes: () => api.get('/rooms/themes/free'),
  getPricing: () => api.get('/rooms/themes/pricing'),
  getAdminThemes: (params) => api.get('/admin/rooms/themes/admin/all', { params }),
  createTheme: (formData) => api.post('/admin/rooms/themes/admin', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateTheme: (id, formData) => api.put(`/admin/rooms/themes/admin/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteTheme: (id) => api.delete(`/admin/rooms/themes/admin/${id}`),
  updatePricing: (data) => api.post('/admin/rooms/themes/admin/pricing', data),
  uploadCustomTheme: (formData) => api.post('/rooms/themes/upload-custom', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  applyTheme: (data) => api.post('/rooms/themes/apply', data),
};

export default api;